/**
 * ----------------------------------------------------------------------------
 * selectors
 * ----------------------------------------------------------------------------
 *
 * This file contains selector functions that return slices of IX2 state in the
 * Designer. They are typically used in reducer logic, or in component logic
 * to display UI.
 *
 * HOW TO ADD UTILITIES TO THIS FILE
 * - If the function requires IX2 state and returns a smaller or derived piece
 *   of state, it belongs here.
 * - If the function does not need IX2 state, consider putting it in `./utils`
 */

import {createSelector} from 'reselect';
import {toJS} from '@packages/systems/core/utils/ImmUtils';
import type {
  IX2StoreStateType as IX2StoreStateTypeImmutable,
  ActionListRecordType,
  EventTRecordypes,
} from '@packages/systems/ix2/designer/records/immutable';

import type {
  IX2List,
  IX2ActionItemType,
  IX2Interactions,
  IX2Interaction,
  IX2StoreStateType,
} from '@packages/systems/ix2/types-core';
import {
  getNodeIdFromIx2Id,
  isQuickEffect,
} from '@packages/systems/ix2/shared-utils';
import {keyframeSort} from '@packages/systems/ix2/shared-utils-IX2Utils';
import {List, RecordOf} from 'immutable';
import {
  ACTION_LISTS,
  EVENTS,
  INTERACTIONS,
  SELECTED_ACTION_ITEM_IDS,
  SELECTED_ACTION_LIST_ID,
  SELECTED_INTERACTION_ID,
} from '@packages/systems/ix2/designer/constants';
import {Expressions} from '@packages/systems/wfdl';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  IX2ContinuousActionGroup,
  IX2TimedActionGroup,
} from '@packages/systems/ix2/shared-constants-IX2Actions';

type IX2StateSelector<ReturnValue> = (
  arg1: IX2StoreStateTypeImmutable
) => ReturnValue;

const selectInteractions = (state: IX2StoreStateTypeImmutable) =>
  state.interactions;
const selectEvents = (state: IX2StoreStateTypeImmutable) => state.events;
const selectActionLists = (state: IX2StoreStateTypeImmutable) =>
  state.actionLists;

const selectSelectedInteractionId = (state: IX2StoreStateTypeImmutable) =>
  state.selectedInteractionId;
const selectSelectedActionListId = (state: IX2StoreStateTypeImmutable) =>
  state.selectedActionListId;

/**
 * ## `selectSelectedInteraction`
 *
 * Returns the currently selected interaction
 */
export const selectSelectedInteraction = createSelector(
  selectInteractions,
  selectSelectedInteractionId,
  (interactions, selectedInteractionId) =>
    selectedInteractionId
      ? interactions.find(({id}) => id === selectedInteractionId)
      : undefined
);

/**
 * ## `selectStagedActionList`
 *
 * Returns the staged action list, used to provide "reset" animation list workflow
 */
export const selectStagedActionList: IX2StateSelector<
  null | ActionListRecordType
> = (state) => state.stagedActionList;

/**
 * ## `selectSelectedActionList`
 *
 * Returns the currently selected action list
 */
export const selectSelectedActionList: IX2StateSelector<
  undefined | ActionListRecordType
> = createSelector(
  selectActionLists,
  selectSelectedActionListId,
  (actionLists, selectedActionListId) =>
    selectedActionListId
      ? actionLists.find(({id}) => id === selectedActionListId)
      : undefined
);

/**
 * ## `selectEventsMap`
 *
 * Returns a map of IX events, keyed by `id`
 */
export const selectEventsMap = createSelector(
  (state: IX2StoreStateType) => state.events,
  (events) => {
    return events.reduce<Record<string, any>>((result, event) => {
      result[event.id] = toJS(event);
      return result;
    }, {});
  }
);

/**
 * ## `selectActionListIdEventMap`
 *
 * Returns a map of actionListIds that are shared by events. Originally used to
 * determine whether an action list is shared by other events, and could
 * therefore affect animations for another interactions.
 */
export const selectActionListIdEventMap = createSelector(
  (state: IX2StoreStateType) => state.events,
  (events) => {
    return events.reduce<Record<string, any>>((result, event) => {
      const actionListId = event.action?.config?.actionListId;

      if (
        actionListId == null ||
        actionListId === '' ||
        isQuickEffect(event.action?.actionTypeId)
      ) {
        return result;
      }

      const set = result[actionListId] || new Set();

      set.add(event.id);
      result[actionListId] = set;

      return result;
    }, {});
  }
);

/**
 * ## `selectActionListsMap`
 *
 * Returns a map of IX actionLists, keyed by `id`
 */
export const selectActionListsMap = createSelector(
  (state: IX2StoreStateType) => state.actionLists,
  (actionLists) => {
    return actionLists.reduce<Record<string, any>>((result, actionList) => {
      result[actionList.id] = actionList;
      return result;
    }, {});
  }
);

/**
 * ## `selectEventNames`
 *
 * Returns a flat list of IX event names
 */
export const selectEventNames = createSelector(
  selectInteractions,
  selectEventsMap,
  (interactions, eventMap) => {
    return interactions.reduce<Array<any>>(
      (names, {eventIds: [firstEventId]}) => {
        const event = eventMap[firstEventId];
        names.push(event.name);
        return names;
      },
      []
    );
  }
);

/**
 * ## `selectEventsPair`
 *
 * Returns an event tuple for a given IX
 */
export const selectEventsPair: IX2StateSelector<null | EventTRecordypes> =
  createSelector(
    selectSelectedInteraction,
    selectEvents,
    (interaction, events) => {
      if (interaction) {
        const eventIds = interaction.eventIds.slice(); // eg [e, e-1] or [e-5]
        const result: Array<any> = [];

        // Get the first event for processing
        let id = eventIds.shift();

        for (const event of events) {
          // Found the event, push the resulting event and shift to the next eventId
          if (id === event.id) {
            result.push(toJS(event));
            id = eventIds.shift();
          }

          // Exit early if we have the event for both event ids
          // @ts-expect-error - TS2352 - Conversion of type 'any[]' to type 'EventTRecordypes' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first. | TS2352 - Conversion of type 'any[]' to type 'EventTRecordypes' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
          if (id == null) return result as EventTRecordypes;
        }
      }

      return null;
    }
  );

/**
 * ## `isSelectedActionListDirty`
 *
 * Returns a boolean that determines whether the animation list was changed.
 * Used to provide "reset" animation list workflow
 */
export const isSelectedActionListDirty: IX2StateSelector<boolean> =
  createSelector(
    selectSelectedActionList,
    selectStagedActionList,
    (selectedActionList, stagedActionList) => {
      if (selectedActionList && stagedActionList) {
        return selectedActionList !== stagedActionList;
      }

      return false;
    }
  );

export const getActionItemsFromGroups = (
  groups: List<typeof IX2ContinuousActionGroup | typeof IX2TimedActionGroup>
): List<IX2ActionItemType> => {
  return (
    groups
      .sort(keyframeSort)
      // @ts-expect-error actionItems missing in statics of RecordInstance (immutable issue)
      .reduce((items, group) => items.concat(group.actionItems), List())
  );
};

export const getActionItemsFromList = (
  actionList: IX2List | null | undefined
): List<RecordOf<IX2ActionItemType>> => {
  // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'IX2List'.
  if (actionList?.continuousParameterGroups != null) {
    // @ts-expect-error - TS2339 - Property 'continuousParameterGroups' does not exist on type 'IX2List'.
    return actionList.continuousParameterGroups.reduce(
      // @ts-expect-error - TS7006 - Parameter 'items' implicitly has an 'any' type. | TS7006 - Parameter 'paramGroup' implicitly has an 'any' type.
      (items, paramGroup) =>
        items.concat(
          getActionItemsFromGroups(paramGroup.continuousActionGroups)
        ),
      List()
    );
    // @ts-expect-error - TS2339 - Property 'actionItemGroups' does not exist on type 'IX2List'.
  } else if (actionList?.actionItemGroups != null) {
    // @ts-expect-error - TS2339 - Property 'actionItemGroups' does not exist on type 'IX2List'.
    return getActionItemsFromGroups(actionList.actionItemGroups);
  }
  return List();
};

export const eventsSelector = (state: IX2StoreStateType) => state.get(EVENTS);
export const actionListsSelector = (state: IX2StoreStateType) =>
  state.get(ACTION_LISTS);
export const interactionsSelector = (state: IX2StoreStateType) =>
  state.get(INTERACTIONS);
export const selectedActionItemIdsSelector = (state: IX2StoreStateType) =>
  state.get(SELECTED_ACTION_ITEM_IDS);
export const selectedActionListIdSelector = (state: IX2StoreStateType) =>
  state.get(SELECTED_ACTION_LIST_ID);
export const selectedInteractionIdSelector = (state: IX2StoreStateType) =>
  state.get(SELECTED_INTERACTION_ID);

export const getSelectedActionListIndex = createSelector(
  actionListsSelector,
  selectedActionListIdSelector,
  (actionLists, selectedActionListId): number =>
    actionLists.findIndex(
      (searchList) => selectedActionListId === searchList.id
    )
);

const getSelectedInteractionListIndex = createSelector(
  interactionsSelector,
  selectedInteractionIdSelector,
  (interactions, selectedInteractionId) =>
    interactions.findIndex(
      (searchList) => selectedInteractionId === searchList.id
    )
);

export const getSelectedActionList = createSelector(
  actionListsSelector,
  getSelectedActionListIndex,
  (actionLists, index): IX2List | null | undefined =>
    index > -1 ? actionLists.get(index) : null
);

export const getSelectedInteraction = createSelector(
  interactionsSelector,
  getSelectedInteractionListIndex,
  (
    interactions: IX2Interactions,
    index: number
  ): IX2Interaction | null | undefined =>
    index > -1 ? interactions.get(index) : null
);

export const getSelectedInteractionTargetElement = (
  ix2State: IX2StoreStateType,
  componentRender: Expression
): ExpressionElement | null => {
  const selectedInteraction = getSelectedInteraction(ix2State);
  return selectedInteraction
    ? Expressions.getElementById(
        componentRender,
        getNodeIdFromIx2Id(selectedInteraction.target)
      )
    : null;
};

export const getSelectedInteractionEvents = createSelector(
  getSelectedInteraction,
  eventsSelector,
  (interaction, events) =>
    interaction
      ? events.filter(({id}) => interaction.eventIds.includes(id))
      : List()
);

export const getSelectedActionItems = createSelector(
  getSelectedActionList,
  selectedActionItemIdsSelector,
  (actionList, selectedItems) => {
    const allListActionItems = getActionItemsFromList(actionList);
    return allListActionItems.filter((item) => selectedItems.contains(item.id));
  }
);
